// Legend / value-label sizing helpers shared by all InteractiveGraph renderers.
//
// MOVED here verbatim from InteractiveGraph.js (module-level pure functions;
// the only change is the `export ` keyword on each moved top-level declaration)
// as maintenance-only Slice 1 of the InteractiveGraph split — same pattern as
// overlayStageVisibility.js (v2.14.19). InteractiveGraph imports
// computeLegendFontScale / legendVScaleCapped / vlScale / getLegendMetrics
// back; legendVScale is consumed only by legendVScaleCapped (and tests).
// Behavior must stay byte-identical with the pre-move code.

import { clampValue, MIN_MARGIN, MIN_LEGEND_WIDTH } from './chartLayout';

export const computeLegendFontScale = (legendMetrics) => {
  if (!legendMetrics) return 1;
  const widthRatio = legendMetrics.defaultWidth
    ? legendMetrics.width / legendMetrics.defaultWidth
    : 1;
  const heightRatio = legendMetrics.defaultHeight
    ? legendMetrics.height / legendMetrics.defaultHeight
    : 1;
  const scale = (widthRatio + heightRatio) / 2 || 1;
  return clampValue(scale, 0.75, 1.35);
};

// 凡例の縦スケール（行間）= 文字サイズ倍率 × 行間倍率。凡例の行を文字サイズに追従させ
// （フォント拡大時の重なりを自動回避）、さらにユーザーが行間スライダーで微調整できる。
// default 1×1=1 のため未設定時は従来挙動（不変）。
export const legendVScale = (layout) => (layout?.legend?.fontScale ?? 1) * (layout?.legend?.lineSpacing ?? 1);
// 凡例の縦倍率 (legendVScale = fontScale × lineSpacing) を、項目数と chart 高さ上限に対して
// 実際に溢れる時だけ自動圧縮する overflow cap (v2.14.9 P3 dynamic-cap)。
// content (= headerOffset + itemCount × perItemStep、最終項目の下端) を box の高さ上限
// (layout.height − 2·MIN_MARGIN、getLegendMetrics の clamp ceiling と同一) 以内に収める fitScale を計算し、
// min(raw, max(1, fitScale)) を返す: raw を超えない / 1 を下回らない / 収まる時は raw のまま (slider 範囲は不変)。
export const legendVScaleCapped = (layout, itemCount, perItemStep, headerOffset = 0) => {
  const raw = legendVScale(layout);
  const n = Math.max(0, Math.floor(itemCount || 0));
  const content = headerOffset + n * perItemStep;
  const ceiling = (layout?.height ?? 0) - MIN_MARGIN * 2;
  if (n < 1 || !(content > 0) || !(ceiling > 0)) return raw;
  return Math.min(raw, Math.max(1, ceiling / content));
};

// データ値ラベル（グラフ要素の上/横に表示される数値）のフォントサイズ倍率。
// 各 renderer の値ラベル font-size に乗算（focus/非focus 比率は保持）。default 1=不変。
export const vlScale = (layout) => layout?.valueLabelScale ?? 1;

export const getLegendMetrics = (layout, itemCount, options = {}) => {
  const legendConfig = layout.legend || {};
  const defaultWidth = clampValue(
    options.defaultWidth ?? legendConfig.width ?? 140,
    MIN_LEGEND_WIDTH,
    layout.width - MIN_MARGIN * 2
  );
  const rowHeight = options.rowHeight ?? 20;
  const visibleItems = Math.min(itemCount, options.maxItems ?? itemCount);
  const computedHeight = clampValue(
    visibleItems * rowHeight + (options.extraHeight ?? 40),
    120,
    layout.height - MIN_MARGIN * 2
  );

  const manual = legendConfig.manual || {};
  const width = clampValue(manual.width ?? legendConfig.width ?? defaultWidth, MIN_LEGEND_WIDTH, layout.width - MIN_MARGIN);
  const height = clampValue(manual.height ?? legendConfig.height ?? computedHeight, 120, layout.height - MIN_MARGIN);

  const position = legendConfig.manualPosition ?? legendConfig.position ?? 'right';

  let x = manual.x ?? legendConfig.x;
  let y = manual.y ?? legendConfig.y;

  if (x == null || y == null) {
    if (position === 'bottom') {
      x = legendConfig.marginLeft ?? layout.margin.left;
      y = layout.height - height - (options.bottomOffset ?? legendConfig.bottomSpacing ?? 24);
    } else {
      x = layout.width - width - (legendConfig.rightOffset ?? 24);
      y = legendConfig.top ?? layout.margin.top;
    }
  }

  return {
    x,
    y,
    width,
    height,
    position,
    heightLimit: computedHeight,
    defaultWidth,
    defaultHeight: computedHeight
  };
};

// countLegendItems — extracted verbatim from InteractiveGraph.js (IG-split
// Slice 4). Pure: counts the legend-relevant elements for a given graph type.
export const countLegendItems = (graphType, actualGraphData) => {
  const elements = actualGraphData?.elements || [];
  if (!elements.length || !graphType) return 0;

  const typeMap = {
    bar_chart: 'bar',
    line_chart: 'line',
    scatter_plot: 'scatter_point',
    histogram: 'histogram_bar',
    box_plot: 'box',
    area_chart: 'area',
    pie_chart: 'pie_slice'
  };

  const targetType = typeMap[graphType];
  if (!targetType) {
    return elements.length;
  }

  return elements.filter((element) => element.type === targetType).length;
};
